import { ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { usePrefixedLocation } from "../hooks/usePrefixedLocation";
import { ROUTE_PREFIX } from "../constants/routes";
import { Button } from "./ui/button";
import { ChevronDown, type LucideIcon } from "lucide-react";
import UserSection from "./UserSection";
import logo from "./../assets/JBF logo.png";

export interface LayoutMenuItem {
  icon: LucideIcon;
  label: string;
  path: string;
  subItems?: {
    label: string;
    path: string;
    icon?: LucideIcon;
    disabled?: boolean;
  }[];
  /** If true, only show this item when user role is super_admin (hide for management) */
  superAdminOnly?: boolean;
  disabled?: boolean;
}

export interface RoleBasedLayoutProps {
  children: ReactNode;
  menuItems: LayoutMenuItem[];
  sidebarTitle: string;
  sidebarSubtitle: string;
}

export default function RoleBasedLayout({
  children,
  menuItems,
  // sidebarTitle,
  // sidebarSubtitle,
}: RoleBasedLayoutProps) {
  const location = useLocation();
  const prefixedLocation = usePrefixedLocation();
  const navigate = usePrefixedNavigate();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // Use pathname relative to app (strips tenant + ROUTE_PREFIX) so active state matches menu paths
  const pathname = location.pathname.includes(ROUTE_PREFIX)
    ? location.pathname.slice(
        location.pathname.indexOf(ROUTE_PREFIX) + ROUTE_PREFIX.length
      ) || "/"
    : prefixedLocation.pathname;

  const isActive = useCallback(
    (path: string) => pathname === path || pathname.startsWith(path + "/"),
    [pathname]
  );

  useEffect(() => {
    const parentWithActiveChild = menuItems.find(item =>
      item.subItems?.some(sub => isActive(sub.path))
    );
    if (parentWithActiveChild) {
      setExpandedItem(parentWithActiveChild.label);
    }
  }, [pathname, menuItems, isActive]);

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Sidebar - same structure for all roles */}
      <aside className="fixed left-0 top-0 z-30 flex h-screen w-[17rem] shrink-0 flex-col overflow-hidden border-r border-border bg-card shadow-sm">
        <div className="flex justify-center border-b border-border p-5">
          <img src={logo} className="w-40" alt="Logo" />
          {/* <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">{sidebarTitle}</h2>
              <p className="text-xs text-muted-foreground">{sidebarSubtitle}</p>
            </div>
          </div> */}
        </div>

        <nav className="flex-1 space-y-1.5 overflow-y-auto p-4">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Menu
          </p>
          {menuItems.map(item => {
            const Icon = item.icon;
            const active =
              isActive(item.path) ||
              (item.subItems?.some(s => isActive(s.path)) ?? false);
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedItem === item.label;

            return (
              <div key={item.label}>
                <Button
                  variant={active ? "default" : "ghost"}
                  disabled={item.disabled}
                  className={`h-11 w-full justify-start gap-3 ${
                    active
                      ? "bg-primary text-white shadow-sm hover:bg-primary/90"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  } ${item.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                  onClick={() => {
                    if (item.disabled) {
                      return;
                    }
                    if (hasSubItems) {
                      setExpandedItem(isExpanded ? null : item.label);
                    } else {
                      navigate(item.path);
                    }
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left font-medium">
                    {item.label}
                  </span>
                  {hasSubItems && (
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  )}
                </Button>

                {hasSubItems && isExpanded && (
                  <div className="mt-0.5 flex min-h-0">
                    <div
                      className="ml-5 mr-3 flex justify-center pb-0 pt-1"
                      aria-hidden
                    >
                      <div className="w-[2px] flex-1 rounded-full bg-gradient-to-b from-primary to-primary/50" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-0.5 pb-1">
                      {item.subItems?.map(subItem => {
                        const SubIcon = subItem.icon;
                        const subActive = isActive(subItem.path);
                        return (
                          <Button
                            key={subItem.path}
                            variant={subActive ? "default" : "ghost"}
                            disabled={subItem.disabled}
                            className={`h-10 w-full justify-start gap-3 text-sm ${
                              subActive
                                ? "border-2 border-primary bg-muted text-black hover:text-white"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            } ${subItem.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                            onClick={() => {
                              if (subItem.disabled) {
                                return;
                              }
                              navigate(subItem.path);
                            }}
                          >
                            {SubIcon && <SubIcon className="h-4 w-4" />}
                            <span className="flex-1 text-left">
                              {subItem.label}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <UserSection />
      </aside>

      {/* Main content — horizontal scroll on small screens when tables are wide */}
      <div className="ml-[17rem] flex min-h-screen min-w-0 flex-1 flex-col">
        <main className="min-w-0 flex-1 overflow-x-auto overflow-y-visible">
          {children}
        </main>
      </div>
    </div>
  );
}
